import { useEffect, useState } from "react";
import { Box, CircularProgress, Link, Typography } from "@mui/material";
import { useFormikContext } from "formik";
import { FormattedMessage, useIntl } from "react-intl";

const SUGGEST_PATH = "/api/price-bounds-rules/suggest/";
const SUGGEST_DEBOUNCE_MS = 400;

type Side = "floor" | "ceiling";

type SuggestResponse = {
  floor: string | null;
  ceiling: string | null;
  n_observations: number;
  lookback_days: number;
  source: "history" | "insufficient_data";
};

/**
 * Inline guidance below a `floor` or `ceiling` field on the
 * PriceBoundsRule form. Watches the scope fields via Formik, debounce-
 * fetches the bounds suggestion endpoint, renders the suggested value
 * plus an Apply link that writes it into this field.
 *
 * The component renders nothing until enough scope is set
 * (property + uh_type_nandc|uh_cat) — keeps the form quiet during early
 * input.
 */
export const SuggestedBoundHint = ({ side }: { side: Side }) => {
  const { values, setFieldValue } = useFormikContext<any>();
  const intl = useIntl();
  const [data, setData] = useState<SuggestResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const property = values?.property?.id ?? values?.property;
  const uhType = values?.uh_type_nandc?.id ?? values?.uh_type_nandc;
  const uhCat = values?.uh_cat?.id ?? values?.uh_cat;
  const dateFrom = values?.date_from;
  const dateTo = values?.date_to;

  useEffect(() => {
    const ctrl = new AbortController();
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (property != null) params.set("property", String(property));
        if (uhType != null) params.set("uh_type_nandc", String(uhType));
        if (uhCat != null) params.set("uh_cat", String(uhCat));
        if (dateFrom) params.set("date_from", dateFrom);
        if (dateTo) params.set("date_to", dateTo);
        const qs = params.toString();
        const url = qs ? `${SUGGEST_PATH}?${qs}` : SUGGEST_PATH;
        const response = await fetch(url, {
          method: "GET",
          credentials: "include",
          signal: ctrl.signal,
        });
        if (!response.ok) {
          setData(null);
          return;
        }
        setData(await response.json());
      } catch (err) {
        if ((err as any)?.name !== "AbortError") setData(null);
      } finally {
        setLoading(false);
      }
    }, SUGGEST_DEBOUNCE_MS);
    return () => {
      ctrl.abort();
      clearTimeout(handle);
    };
  }, [property, uhType, uhCat, dateFrom, dateTo]);

  if (loading && !data) {
    return (
      <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
        <FormattedMessage
          id="PRICE_BOUNDS_SUGGEST_LOADING"
          defaultMessage="Loading suggestion…"
        />
      </Typography>
    );
  }
  if (!data) return null;

  if (data.source === "insufficient_data") {
    return (
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 0.5 }}>
        <Typography variant="caption" sx={{ color: "text.disabled" }}>
          <FormattedMessage
            id="PRICE_BOUNDS_SUGGEST_INSUFFICIENT"
            defaultMessage="Not enough history ({n} prices). Set manually."
            values={{ n: data.n_observations }}
          />
        </Typography>
        {loading && <CircularProgress size={10} />}
      </Box>
    );
  }

  const suggestion = side === "floor" ? data.floor : data.ceiling;
  if (suggestion == null) return null;

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "baseline", mt: 0.5 }}>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        <Box component="span" sx={{ fontWeight: 600, mr: 0.5 }}>
          <FormattedMessage id="HINT_LABEL" defaultMessage="Hint:" />
        </Box>
        {side === "floor" ? (
          <FormattedMessage
            id="PRICE_BOUNDS_SUGGEST_HINT_FLOOR"
            defaultMessage="5% of {n} sold prices were below {value} €"
            values={{ value: suggestion, n: data.n_observations }}
          />
        ) : (
          <FormattedMessage
            id="PRICE_BOUNDS_SUGGEST_HINT_CEILING"
            defaultMessage="5% of {n} sold prices were above {value} €"
            values={{ value: suggestion, n: data.n_observations }}
          />
        )}
      </Typography>
      <Link
        component="button"
        type="button"
        variant="caption"
        onClick={() => setFieldValue(side, suggestion)}
        sx={{ cursor: "pointer" }}
      >
        {intl.formatMessage({
          id: "PRICE_BOUNDS_SUGGEST_APPLY",
          defaultMessage: "Apply",
        })}
      </Link>
      {loading && <CircularProgress size={10} />}
    </Box>
  );
};
